"use client";

import { TypeImageCollection } from "@contentful/types";
import { faArrowRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { customImageLoader } from "@yardzen/next-client-util";
import { Button } from "@yardzen/ui/components/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@yardzen/ui/components/carousel";
import { H1 } from "@yardzen/ui/components/h1";
import { ImageWithAvatarCard } from "@yardzen/ui/components/image-with-avatar-card";
import { Link } from "@yardzen/ui/components/link";
import { P } from "@yardzen/ui/components/p";
import { cn } from "@yardzen/ui/lib/utils";
import { cva } from "class-variance-authority";
import AutoScroll from "embla-carousel-auto-scroll";
import Image from "next/image";
import { useRouter } from "next/navigation";

/**
 * Similar to Hero:variant: "slider"
 * Eventually this will replace <PhotoSliderCarousel> as we move away from Hero that behaves like an ImageCollection
 */
export function ImageCollectionSlideshow({
  imageCollection,
  titleClassName,
}: {
  imageCollection: TypeImageCollection<"WITHOUT_UNRESOLVABLE_LINKS">;
  titleClassName?: string;
}) {
  const router = useRouter();
  const {
    subheading,
    heading,
    images,
    navigationOptions,
    cta,
    numberOfDesktopColumns,
  } = imageCollection.fields;
  const autoScroll = navigationOptions?.find((o) => o === "auto-scrolling");
  let carouselImages = images;
  if (autoScroll && carouselImages && carouselImages.length <= 6) {
    carouselImages = [...carouselImages, ...carouselImages];
  }

  const variants = cva("", {
    variants: {
      imageWidth: {
        greaterFour: "sm:basis-1/4 lg:basis-1/6",
        fourOrLess: "sm:basis-[30%]",
      },
    },
  });
  const imageCountToVariant =
    (numberOfDesktopColumns || 6) > 4 ? "greaterFour" : "fourOrLess";

  return (
    <div className="relative">
      <div className="mb-10 flex flex-col items-center text-center">
        {heading && (
          <H1
            className={cn(
              "text-brand-primary-darker mb-6 px-4 pt-12 text-4xl md:text-6xl lg:text-6xl",
              titleClassName,
            )}
          >
            {heading}
          </H1>
        )}
        {subheading && (
          <P
            noSpacing
            className="text-brand-moss-greens-90 w-[90%] pb-8 text-base md:min-w-[37%] md:max-w-[65%] md:text-xl"
          >
            {subheading}
          </P>
        )}
        <Carousel
          className="w-full"
          plugins={
            autoScroll
              ? [
                  AutoScroll({
                    playOnInit: true,
                    stopOnInteraction: false,
                    speed: 0.5,
                  }),
                ]
              : []
          }
          opts={{
            loop: true,
          }}
        >
          <CarouselContent className="-ml-1" aria-live="off">
            {carouselImages?.map((image, index) => {
              const imageUrl = image?.fields?.image?.fields?.file?.url;
              const alt =
                image?.fields?.image?.fields?.description ||
                `${image?.fields?.image?.fields?.title}`;
              const avatarUrl = image?.fields?.avatar?.fields?.file?.url;
              const avatarAlt =
                image?.fields?.avatar?.fields?.description ||
                `${image?.fields?.avatar?.fields?.title}`;
              if (!imageUrl) {
                return null;
              }
              return (
                <CarouselItem
                  key={`slideshow-${image.sys.id}-${index}`}
                  className={cn(
                    "basis-2/3",
                    variants({
                      imageWidth: imageCountToVariant,
                    }),
                  )}
                >
                  <Link
                    variant="nav"
                    onClick={() => {
                      const url = image.fields.link;
                      if (url) {
                        router.push(url);
                      }
                    }}
                  >
                    {avatarUrl ? (
                      <ImageWithAvatarCard
                        imageUrl={imageUrl}
                        imageAlt={alt}
                        avatarUrl={avatarUrl}
                        avatarAlt={avatarAlt}
                        cardHeading={image?.fields?.image?.fields?.title}
                        cardSubheading={image?.fields?.avatar?.fields?.title}
                        linkUrl={image?.fields?.link}
                        linkText={image?.fields?.icon?.toLocaleUpperCase()}
                      />
                    ) : (
                      <Image
                        src={`https:${imageUrl}`}
                        alt={alt ?? ""}
                        height={750}
                        width={600}
                        className="h-full w-full rounded-[0.68981rem] object-cover"
                        loading="lazy"
                        loader={customImageLoader}
                        sizes="(max-width: 640px) 66vw,(max-width: 1024px) 25vw, 20vw"
                      />
                    )}
                  </Link>
                </CarouselItem>
              );
            })}
          </CarouselContent>
        </Carousel>
        {cta && cta.fields.buttonText && cta.fields.url && (
          <div className="mt-10 flex flex-row justify-center">
            <Button
              variant={cta.fields.variant}
              className="w-full sm:w-auto"
              endIcon={<FontAwesomeIcon icon={faArrowRight} />}
              onClick={() => {
                if (!cta?.fields.url) return;
                router.push(cta.fields.url);
              }}
            >
              {cta?.fields.buttonText}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
